/**
 * Provides a gui for uploading images in bulk.
 * @module gui/pages
 */
import React from 'react'
import assign from 'lodash/assign'
import compact from 'lodash/compact'
import concat from 'lodash/concat'
import { createStore, combineReducers, applyMiddleware, compose } from 'redux'
import { logger as reduxLoggerMiddleware } from 'redux-logger'
import { Provider } from 'react-redux'
import { createRoot } from 'react-dom/client'
import { createBrowserHistory } from 'history'
import { Map } from 'immutable'
import { LicenseInfo } from '@mui/x-license'
import { getAuthLocal, setAuthLocal, getAuthJwt, isLoggedIn, renew } from 'services/auth'
import { getConfig, getConfigDataServer } from 'services/config'
// Middleware and state
import configReducer, { initialState as configInitialState } from 'services/config/redux/reducers'
import dataReducer, { initialState as dataInitialState } from 'services/data/redux/reducers'
import locationReducer, {
  initialState as locationInitialState,
} from 'services/location/redux/reducers'
import authReducer, { initialState as authInitialState } from 'services/auth/redux/reducers'
import testarossaReducer, {
  initialState as testarossaInitialState,
} from 'services/testarossa/redux/reducers'
import userAbuseReducer, {
  initialState as userAbuseInitialState,
} from 'services/user-abuse/redux/reducers'
import userModerationReducer, {
  initialState as userModerationInitialState,
} from 'services/user-moderation/redux/reducers'
import menusReducer, { initialState as menusInitialState } from 'services/menu/redux/reducers'
import commentsReducer, {
  initialState as commentsInitialState,
} from 'services/comments/redux/reducers'
import titlesReducer, { initialState as titlesInitialState } from 'services/titles/redux/reducers'
import modalReducer, { initialState as modalInitialState } from 'services/modal/redux/reducers'
import toastReducer, { initialState as toastInitialState } from 'services/toast/redux/reducers'
import bonusSkuReducer, {
  initialState as bonusSkuInitialState,
} from 'services/plans/redux/reducers'
import { createMiddleware as createWitnessMiddleware } from 'redux-witness'
// New middleware pattern for Redux-Witness
import * as authWatchers from 'services/auth/watchers'
import * as commentsWatchers from 'services/comments/watchers'
import * as configWatchers from 'services/config/watchers'
import * as contentFilterSetsWatchers from 'services/content-filter-sets/watchers'
import * as contentFiltersWatchers from 'services/content-filters/watchers'
import * as contentWatchers from 'services/content/watchers'
import * as contentPolicyWatchers from 'services/content-policy/watchers'
import * as imagesWatchers from 'services/images/watchers'
import * as locationWatchers from 'services/location/watchers'
import * as newrelicWatchers from 'services/newrelic/watchers'
import * as mediaWatchers from 'services/media/watchers'
import * as mediaAssetsWatchers from 'services/media-assets/watchers'
import * as pagesWatchers from 'services/pages/watchers'
import * as pmSectionWatchers from 'services/pm-section/watchers'
import * as testarossaWatchers from 'services/testarossa/watchers'
import * as titlesWatchers from 'services/titles/watchers'
import * as zuoraWatchers from 'services/zuora/watchers'
import * as userAbuseWatchers from 'services/user-abuse/watchers'
import * as userModerationWatchers from 'services/user-moderation/watchers'
import * as userWatchers from 'services/user/watchers'
import * as menuWatchers from 'services/menu/watchers'
import * as financeWatchers from 'services/finance/watchers'
import * as modalWatchers from 'services/modal/watchers'
import * as ambassadorsWatchers from 'services/ambassador/watchers'
import * as giftWatchers from 'services/gift-management/watchers'

// Actions
import { setConfigData as setConfigDataAction } from 'services/config/redux/actions'
import { setLocation as setLocationAction } from 'services/location/redux/actions'
import pageReducer, { initialState as pageInitialState } from 'services/pages/redux/reducers'
import Router from '../components/router'

function renderApp ({ store, children, history }) {
  return (
    <Provider store={store}>
      <Router history={history}>{children}</Router>
    </Provider>
  )
}

/**
 * Start function which provides shared middleware,
 * state, and reducers.  Other common functionality
 * such as dev tools is here too. Page specific middlware,
 * state and reducers are provided via options.
 *
 * @see https://chrome.google.com/webstore/detail/immutablejs-object-format/hgldghadipiblonfkkicmgcbbijnpeog
 * If you are looking to debug immutable objects in the brower console immutable dev tools
 * has been replaced with a chrom extension
 */
export async function start (options = {}) {
  let { middleware = [], reducers = {}, initialState = {} } = options
  const { preRender = async () => {}, postRender = async () => {} } = options
  const { children } = options
  const { location } = global
  const reduxDexToolsCompose = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  const composeEnhancers = reduxDexToolsCompose || compose
  const history = createBrowserHistory()
  const witnessMiddleware = createWitnessMiddleware()

  try {
    // Renew a JWT if we have one, but it is expired
    const configData = await getConfig()
    const auth = Map({ data: getAuthLocal() || Map() })
    const config = Map({ data: configData })
    if (getAuthJwt(auth) && !isLoggedIn(auth)) {
      // The JWT exists, but is expire so renew now and save it locally
      // to limit startup thrashing
      const authRenewed = await renew({
        server: getConfigDataServer(config),
        auth,
      })
      if (authRenewed.get('success')) {
        setAuthLocal(authRenewed)
      }
    }
    // apply watcher functions for middleware
    witnessMiddleware.run(
      configWatchers,
      authWatchers,
      newrelicWatchers,
      commentsWatchers,
      pagesWatchers,
      locationWatchers,
      contentWatchers,
      contentPolicyWatchers,
      mediaWatchers,
      mediaAssetsWatchers,
      testarossaWatchers,
      pmSectionWatchers,
      zuoraWatchers,
      userAbuseWatchers,
      userModerationWatchers,
      userWatchers,
      imagesWatchers,
      contentFiltersWatchers,
      contentFilterSetsWatchers,
      titlesWatchers,
      menuWatchers,
      financeWatchers,
      modalWatchers,
      ambassadorsWatchers,
      giftWatchers,
    )

    middleware = compact(
      concat(
        [reduxDexToolsCompose ? undefined : reduxLoggerMiddleware, witnessMiddleware],
        middleware,
      ),
    )

    reducers = assign(
      {},
      {
        config: configReducer,
        auth: authReducer,
        data: dataReducer,
        location: locationReducer,
        page: pageReducer,
        testarossa: testarossaReducer,
        userAbuse: userAbuseReducer,
        userModeration: userModerationReducer,
        menus: menusReducer,
        comments: commentsReducer,
        titles: titlesReducer,
        modal: modalReducer,
        toast: toastReducer,
        bonusSku: bonusSkuReducer,
      },
      reducers,
    )

    initialState = assign(
      {},
      {
        config: configInitialState,
        auth: authInitialState,
        data: dataInitialState,
        location: locationInitialState,
        page: pageInitialState,
        testarossa: testarossaInitialState,
        userAbuse: userAbuseInitialState,
        userModeration: userModerationInitialState,
        menus: menusInitialState,
        comments: commentsInitialState,
        titles: titlesInitialState,
        modal: modalInitialState,
        toast: toastInitialState,
        bonusSku: bonusSkuInitialState,
      },
      initialState,
    )

    const store = createStore(
      combineReducers(reducers),
      initialState,
      composeEnhancers(applyMiddleware(...middleware)),
    )
    // Add APP_BOOTSTRAP_PHASE_INIT
    store.dispatch(setConfigDataAction({ data: configData, processing: false }))
    store.dispatch(setLocationAction({ location }))
    // Listen for changes to the current location.
    history.listen(() => {
      // location is an object like window.location
      store.dispatch(
        setLocationAction({ location: { href: `${location.pathname}${location.search || ''}` } }),
      )
    })
    // Add APP_BOOTSTRAP_PRE_RENDER
    await preRender()
    // License key for MUI X Pro
    LicenseInfo.setLicenseKey(config.getIn(['data', 'mui', 'licenseKey']))
    const root = createRoot(document.getElementById('app'))
    root.render(renderApp({ store, children, history }))
    // Add APP_BOOTSTRAP_POST_RENDER
    await postRender()
    // Add APP_BOOTSTRAP_COMPLETE
    // return { app, store }
  } catch (e) {
    console.error(e) // eslint-disable-line no-console
  }
  // return null
}
